import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  IGenericServiceResponse,
  IServiceResponse,
} from '../interfaces/IServiceResponse';
import { environment } from 'src/environments/environment';
import { EpisodeOfCareService } from './episodeOfCare.service';
import { PatientService } from './patient.service';
import { saveAs } from 'file-saver';
import { IAdmissionFile } from '../interfaces/AdmissionPatient/AdmissionProfile/IAdmissionFile';
import { IAddDocument } from '../interfaces/AdmissionPatient/AdmissionProfile/IAddDocument';
import { TranslateService } from '@ngx-translate/core';
import { SwalToastService } from './swal.service';

@Injectable({ providedIn: 'root' })
export class SupportingDocumentsService {
  public supportingDocumentModalSub = new BehaviorSubject<boolean>(false);
  public supportingDocumentEditSub = new BehaviorSubject<
    IAdmissionFile | undefined
  >(undefined);
  public supportingDocumentDataSub = new BehaviorSubject<IAdmissionFile[]>([]);
  public supportingDocumentLoadingSub = new BehaviorSubject<boolean>(false);
  public supportingDocumentLoadingSub$ =
    this.supportingDocumentLoadingSub.asObservable();

  constructor(
    private readonly _http: HttpClient,
    private readonly eocService: EpisodeOfCareService,
    private readonly patientService: PatientService,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService
  ) {}

  async getData() {
    this.supportingDocumentLoadingSub.next(true);
    await this.getSupportingDocumentFiles(
      this.patientService.patientIDsignal()
    ).subscribe({
      next: (response) => {
        this.supportingDocumentDataSub.next(response?.data!);
        this.supportingDocumentLoadingSub.next(false);
      },
      error: (err) => {
        this.swalToastService.toastError(
          'Unable to retrieve Supporting Documents'
        );
        console.error('Error fetching Supporting Documents files:', err);
      },
    });
  }

  getSupportingDocumentFiles(
    patientId: string
  ): Observable<IGenericServiceResponse<IAdmissionFile[]>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this._http.get<IGenericServiceResponse<IAdmissionFile[]>>(
      `${environment.BACKEND_URL}Patient/Files/${patientId}`,
      { headers }
    );
  }

  getPDF(fileId: string): Observable<Blob> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this._http.get(
      `${
        environment.BACKEND_URL
      }Patient/DownloadPatientFile/${fileId}/${this.patientService.patientIDsignal()}`,
      {
        headers: headers,
        responseType: 'blob',
      }
    );
  }

  downloadPDF(item: IAdmissionFile): void {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    this._http
      .get(
        `${environment.BACKEND_URL}Patient/DownloadPatientFile/${
          item.id
        }/${this.patientService.patientIDsignal()}`,
        {
          headers: headers,
          responseType: 'blob',
        }
      )
      .subscribe(
        (blob) => {
          const file = new Blob([blob], { type: 'application/pdf' });
          saveAs(file, `${item.fileType}.pdf`);
        },
        (error) => {
          this.swalToastService.toastError('Unable to download the document');
          console.error('Error downloading PDF:', error);
        }
      );
  }

  addSupportingDocumentFile(documentObj: IAddDocument) {
    const formData = new FormData();

    // TO BE CHANGED
    if (documentObj?.files) {
      documentObj.files.forEach((file, index) => {
        formData.append(`Files`, file);
      });
    }

    formData.append('FileTypeId', documentObj?.fileTypeId!);
    formData.append('PatientId', this.patientService.patientIDsignal());

    return this._http.post(
      `${environment.BACKEND_URL}Patient/UploadPatientFile`,
      formData
    );
  }

  deleteSupportingDocumentFile(fileId: string): Observable<IServiceResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.delete<IServiceResponse>(
      `${
        environment.BACKEND_URL
      }Patient/DeletePatientFile/${fileId}/${this.patientService.patientIDsignal()}`,
      { headers: headers }
    );
  }
}
